import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CardHeader, FormControl, MenuItem, OutlinedInput, Select } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import OrderProductsTable from '../OrderProductsTable/OrderProductsTable';
import { formatNumber } from '../../../constants.js/misc';

// Assets
import calendar from '../../../assets/icons/calendar2.svg';
import { useFieldArray, useForm } from "react-hook-form";
import { parseDateToEdit } from '../../../constants.js/orders';
import OrderProductsTableToEdit from '../OrderProductsTable/OrderProductsTableToEdit';
import { autocompleteUserStoresService } from '../../../services/store';
import { useState } from 'react';
import { DivContainerTable } from '../../../constants.js/containers';

const useStyles = makeStyles({
    total: {
        padding: '16px',
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-end'
    }
});

const OrderDetailsTable = ({ orderDetails, order, storeData, editMode, generalData, setGeneralData, submitProductsTable }) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const options = { month: "long", day: "numeric" };

    const creationDate = new Date(order?.created_at);
    const dateCreation = creationDate.toLocaleDateString("es-ES", options);
    const yearCreation = creationDate.toLocaleDateString("es-ES", { year: "numeric" });
    const hourCreation = creationDate.toLocaleTimeString();

    const creationDateToEdit = parseDateToEdit(order?.created_at, false);

    const [userStores, setUserStores] = useState([])

    const getDeliveryCost = () => {
        // If order has specific shipping cost (and it's not 0 or null), use it
        if (order?.shipping_cost && order.shipping_cost !== 0) {
            const orderCost = Number(order.shipping_cost);
            return isNaN(orderCost) ? 0 : orderCost;
        }

        // Otherwise, check store delivery settings
        if (!storeData?.delivery_cost_enabled) {
            return 0;
        }

        const storeCost = Number(storeData.delivery_cost_details);
        return isNaN(storeCost) ? 0 : storeCost;
    };

    const productSubtotal = orderDetails.reduce((prev, item) => {
        const price = item?.product?.discount_price || item?.price || 0;
        const totalPrice = item?.quantity * price
        return prev + totalPrice;
    }, 0);

    const deliveryCost = getDeliveryCost();
    const grandTotal = productSubtotal + deliveryCost;

    useEffect(() => {
        editMode &&
            autocompleteUserStoresService(storeData?.id)
                .then(res => {
                    setUserStores(res.data);
                })
                .catch(err => {
                    console.log(err)
                })
    }, [editMode, storeData])

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors }
    } = useForm({
        defaultValues: {
            products: orderDetails?.map(products => products),
        },
        mode: 'all',
    });

    // If the product existed in the order
    const updateOptions = (opts, variantsSelected) => {
        let id = []

        const optOpt2 = opts?.map(opt => opt?.options)
        const arrOpts2 = optOpt2?.map(opt => opt?.filter(opt =>
            variantsSelected?.includes(opt?.name)
        ))
        arrOpts2.map(sOpt => id.push(sOpt[0]?.id))

        return id
    }

    const case2WithOpts = (opts, variantsSelected, id) => {
        const optOpt = opts?.map(opt => opt?.options)
        const arrOpts = optOpt?.map(opt => opt?.filter(opt =>
            variantsSelected?.includes(opt?.name)
        ))
        arrOpts.map(sOpt => id.push(sOpt[0]?.id))
    }

    const parseOptions = (opts, method, variantsSelected, oldOptions) => {
        let id = []
        switch (method) {
            case 1:
                oldOptions.length > 0
                    ? id = oldOptions?.map(optc => optc?.product_option_value_id)
                    : id = updateOptions(opts, variantsSelected)
                break;
            case 2:
                Array.isArray(opts)
                    ? case2WithOpts(opts, variantsSelected, id)
                    : id = []
                break;
            case 3: //Reserva para cuando ya existe el producto en la orden
                id = opts?.map(opt => opt?.product_option_value_id)
                break;
            default:
                break;
        }
        return id
    }

    const parseVariant = (variants, variantsSelected) => {
        const id = (variants?.find(element => JSON.stringify(element?.options) === JSON.stringify(variantsSelected))?.id || 0)
        return id
    }

    const normalizeData = (data) => {
        // To get options selected of products
        const optionsSelected = data?.order_item_option
            ? parseOptions(data?.product?.product_options, 1, data?.variants, data?.order_item_option)
            : parseOptions(data?.optionsProduct, 2, data?.variants)

        return {
            options: optionsSelected,
            product_id: data?.product_id,
            product_variant_id: data?.product_variant_id || parseVariant(data?.variantsProduct, data?.variants),
            quantity: parseInt(data?.quantity),
            price: data?.price
        }
    }

    const saveInfo = (data) => {
        setGeneralData({ ...generalData, CreationDate: watch('newCreationDate'), line: watch('line'), shippingCost: watch('shipping_cost'), dataProducts: data?.products?.map(prod => normalizeData(prod)) })
    }

    return (
        <Card elevation={2} style={{ marginBottom: '21px' }}>
            <CardHeader titleTypographyProps={{ variant: 'h6' }} title={t('OrderDetail.detailsTitle')} style={{ paddingBottom: '0' }} />
            <CardContent style={{ padding: 0 }}>
                <Grid container>
                    <div style={{ padding: '10px 16px 16px 16px' }}>
                        {!editMode
                            ? <>
                                <img src={calendar} alt="calendar" style={{ width: '14px' }} /> {`${dateCreation}, ${yearCreation} - ${hourCreation}`}
                                <Typography variant="body1" color='textSecondary'>Canal de venta: <span style={{ fontWeight: 700 }}>{order?.user_store?.mobile_phone_number}</span> </Typography>
                            </>
                            : <>
                                <FormControl
                                    variant="outlined"
                                    margin="normal"
                                    defaultValue={creationDateToEdit}
                                    fullWidth>
                                    <OutlinedInput
                                        defaultValue={creationDateToEdit}
                                        type="datetime-local"
                                        {...register("newCreationDate")}
                                        onMouseOut={editMode && saveInfo}
                                        fullWidth />
                                </FormControl>

                                <Select
                                    defaultValue={order?.user_store?.id}
                                    {...register("line")}
                                    onClick={editMode && saveInfo}
                                    fullWidth
                                    variant="outlined"
                                    disableUnderline
                                >
                                    {
                                        userStores?.map(userStore => (
                                            <MenuItem
                                                key={userStore?.id}
                                                value={userStore?.id}
                                                defaultValue={order?.user_store?.id}
                                            >
                                                {userStore?.mobile_phone_number}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </>
                        }
                    </div>
                    {!editMode
                        ? <>
                            <DivContainerTable>
                                <OrderProductsTable orderDetails={orderDetails} />
                            </DivContainerTable>
                            <div className={classes.total}>
                                <div style={{ marginRight: '1rem' }}>
                                    <Typography gutterBottom variant="body1">Subtotal:</Typography>
                                    <Typography gutterBottom variant="body1">Costos de envio:</Typography>
                                    <Typography gutterBottom variant="body1">Total:</Typography>
                                </div>
                                <div>
                                    <Typography gutterBottom variant="body1">{formatNumber(productSubtotal, storeData.currency)} {storeData?.currency}</Typography>
                                    <Typography gutterBottom variant="body1">{formatNumber(getDeliveryCost(), storeData.currency)}</Typography>
                                    <Typography gutterBottom variant="body1">{formatNumber(grandTotal, storeData.currency)} {storeData?.currency}</Typography>
                                </div>
                            </div>
                        </>
                        : <DivContainerTable>
                            <OrderProductsTableToEdit
                                displayBlock
                                deliveryCost={getDeliveryCost()}
                                control={control}
                                register={register}
                                watch={watch}
                                handleSubmit={handleSubmit}
                                setValueShipping={setValue}
                                useFieldArray={useFieldArray}
                                saveInfo={saveInfo}
                                submitProductsTable={submitProductsTable}
                            />
                        </DivContainerTable>
                    }
                </Grid>
            </CardContent>
        </Card>
    );
};

const mapStateToProps = ({ common }) => ({
    storeData: common.storeData
});

export default connect(
    mapStateToProps
)(OrderDetailsTable);